/* Which map sprite a player is wearing.

   The server resolves this — `PublicUserSerializer.equipped_cosmetics` fills in every slot and
   falls back for anything retired or unowned, so what arrives here is always drawable. This helper
   exists so the two places that build map elements (the game and the lobby) read it the same way,
   and so a payload from an older server, which carries no cosmetics at all, still draws.

   Keyed by slot and then by side: a player owns one look per team, because their icon only shows
   in the games where they are that side. */

export const UNIT_ICON = "unit_icon"

export const unitIconFor = (user, team) =>
  user?.equippedCosmetics?.[UNIT_ICON]?.[team] ||
  user?.equipped_cosmetics?.[UNIT_ICON]?.[team] ||
  `${team}_default`

/* What a *seat* draws as. The colour is chosen in the lobby and copied onto the game, so it wins
   over the account's equipped look: a game is played in the colours its lobby agreed on, and
   editing a profile afterwards must not repaint a replay of it (owner's ask, 2026-09-04).

   Falls through to the account's look for games that started before seats had a colour, and to the
   side's default after that — a map with a missing sprite is worse than a map with the plain one. */
export const seatIconFor = (seat, ownEquipped = null) =>
  seat?.unitIcon ||
  seat?.unit_icon ||
  (ownEquipped ? unitIconFor({ equippedCosmetics: ownEquipped }, seat?.team) : null) ||
  unitIconFor(seat?.user, seat?.team)

/* The player's picture: one id, worn by the account and not per side (owner, 2026-10-08). The
   server resolves it like every slot — an account that never chose one is given one of the four
   free faces by its id — so the fallback here is only for a payload with no cosmetics at all. */
export const AVATAR = "avatar"
export const DEFAULT_AVATAR = "avatar_soldier"

export const avatarFor = (user) => {
  const worn = user?.equippedCosmetics?.[AVATAR] || user?.equipped_cosmetics?.[AVATAR]
  return typeof worn === "string" ? worn : DEFAULT_AVATAR
}

/* What a *seat* shows as its person (owner, 2026-10-09). A person's own picture, always. A bot
   never chose one — the server hands every account a free face by its id, which seats a soldier on
   the horde's side as often as not — so a bot wears a free face of the side it plays, the man or
   the woman by its id, and the same bot keeps the same face. */
const SIDE_FACES = {
  human: ["avatar_soldier", "avatar_soldier_f"],
  zombie: ["avatar_zombie", "avatar_zombie_f"],
}

export const seatPictureFor = (seat, ownAvatar = null) => {
  // Your own seat reads the wallet's picture when it has one: the shop updates it the moment you
  // choose, and the lobby and game payloads only catch up on their next broadcast.
  if (ownAvatar) return ownAvatar
  const user = seat?.user
  const isBot = user?.isBot ?? user?.is_bot
  const faces = SIDE_FACES[seat?.team]
  if (isBot && faces) return faces[Math.abs(Number(user?.id) || 0) % faces.length]
  return avatarFor(user)
}

export default unitIconFor
